// Ispis radnog naloga ZA POGON.
//
// Papir koji ide majstoru, a ne u knjigovodstvo. Razlikuje se od postojeca dva
// ispisa (DocumentProductionPrint i ...NoPrice) po tome sto:
//   - NE ispisuje nijednu cijenu ni iznos; na stolu u pogonu nemaju sto traziti
//     (ispis "NoPrice" i dalje nosi stupce "Dob.cij." i "Dob.iznos"),
//   - uz materijal ima kvadratic za odvagu i prazan stupac "Izdano", da se
//     upisuje stvarno izdana kolicina,
//   - uz proizvode ima prazan stupac "Stvarno izradeno",
//   - ima mjesto za napomenu i dva potpisa: tko je materijal izdao i tko je
//     izradu preuzeo.
//
// Stavke se razvrstavaju po "FreeF6", kako to radi i sam ekran radnog naloga
// (DocumentDetailList3/31/32/33):
//   3 materijal, 31 rad, 32 ostalo, 33 proizvedeno.
// Provjereno na podacima: 14.628 redaka ima 3 i svi su izlaz, 246 ih ima 33 i
// svi su ulaz.

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import dayjs from 'dayjs'

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs

// Oznake skupina na stavci radnog naloga.
export const MATERIJAL = 3
export const RAD = 31
export const OSTALO = 32
export const PROIZVEDENO = 33

const CRTA = '#9e9e9e'
const ZAGLAVLJE = '#eeeeee'

const broj = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

// Kolicina se pise bez nepotrebnih decimala: 12 ostaje 12, a 12,5 zadrzava.
const kol = (v) => {
  const n = broj(v)
  const dec = Math.abs(n - Math.round(n)) < 0.0001 ? 0 : 2
  return n.toFixed(dec).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

const tekst = (v) => (v === null || v === undefined ? '' : String(v))
const datum = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '')

// Je li nalog razvrstan. Stavke bez oznake postoje (usluzne, bez kolicine), pa
// se gleda ima li ijedna stavka upisanu oznaku skupine.
export const razvrstan = (stavke) =>
  (Array.isArray(stavke) ? stavke : []).some(s => s && Math.round(broj(s.FreeF6)) > 0)

/**
 * Stavke jedne skupine.
 *
 * Kad je nalog razvrstan, ide po oznaci "FreeF6". Kad NIJE - stari ili rucno
 * unesen nalog - razvrstava se po kolicini: sto izlazi je materijal, sto ulazi
 * je proizvod. Bez toga bi papir za takav nalog bio prazan iako ima stavke.
 */
const skupina = (stavke, oznaka) => {
  const sve = Array.isArray(stavke) ? stavke : []
  if (razvrstan(sve)) {
    return sve.filter(s => s && Math.round(broj(s.FreeF6)) === oznaka)
  }
  if (oznaka === MATERIJAL) { return sve.filter(s => s && broj(s.itemPcsMinus) !== 0) }
  if (oznaka === PROIZVEDENO) { return sve.filter(s => s && broj(s.itemPcsPlus) !== 0) }
  // Rad i ostalo se bez oznake ne mogu prepoznati, pa se ne pogadaju.
  return []
}

// Kolicina se na materijalu vodi kao izlaz, na proizvodima kao ulaz; neutralna
// se uzima kad ni jedno ni drugo nije popunjeno.
const kolicina = (s) =>
  broj(s.itemPcsMinus) || broj(s.itemPcsPlus) || broj(s.itemPcsNeutral)

// Kvadratic za odvagu. Crta se kao celija s rubom, bez teksta - tako se i
// otisne na obicnom pisacu, bez posebnog fonta.
const kvadratic = () => ({
  table: {widths: [8], body: [[{text: ' ', margin: [0, 0, 0, 0]}]]},
  layout: {
    hLineWidth: () => 0.8, vLineWidth: () => 0.8,
    hLineColor: () => '#555', vLineColor: () => '#555',
    paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 2, paddingBottom: () => 2
  },
  margin: [0, 1, 0, 1]
})

const zaglavljeCelija = (t, poravnanje) => ({
  text: t,
  bold: true,
  fontSize: 8,
  alignment: poravnanje || 'left',
  fillColor: ZAGLAVLJE,
  margin: [2, 3, 2, 3]
})

const celija = (t, poravnanje) => ({
  text: t,
  fontSize: 9,
  alignment: poravnanje || 'left',
  margin: [2, 3, 2, 3]
})

const okvir = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => CRTA,
  vLineColor: () => CRTA,
  paddingLeft: () => 2,
  paddingRight: () => 2,
  paddingTop: () => 0,
  paddingBottom: () => 0
}

const naslovSkupine = (t) => ({
  text: t,
  fontSize: 11,
  bold: true,
  margin: [0, 14, 0, 4]
})

// Materijal: kvadratic, sifra, naziv, JM, trazena kolicina, prazno za izdano.
const tablicaMaterijal = (stavke) => ({
  table: {
    headerRows: 1,
    widths: [16, 70, '*', 32, 60, 70],
    body: [[
      zaglavljeCelija(''), zaglavljeCelija('Šifra'), zaglavljeCelija('Naziv'),
      zaglavljeCelija('JM'), zaglavljeCelija('Količina', 'right'),
      zaglavljeCelija('Izdano', 'center')
    ]].concat(stavke.map(s => [
      kvadratic(),
      celija(tekst(s.itemID)),
      celija(tekst(s.itemName)),
      celija(tekst(s.itemUnit)),
      celija(kol(kolicina(s)), 'right'),
      celija(' ')
    ]))
  },
  layout: okvir
})

// Proizvodi: sifra, naziv, JM, planirano, prazno za stvarno izradeno.
const tablicaProizvodi = (stavke) => ({
  table: {
    headerRows: 1,
    widths: [70, '*', 32, 66, 86],
    body: [[
      zaglavljeCelija('Šifra'), zaglavljeCelija('Naziv'), zaglavljeCelija('JM'),
      zaglavljeCelija('Planirano', 'right'), zaglavljeCelija('Stvarno izrađeno', 'center')
    ]].concat(stavke.map(s => [
      celija(tekst(s.itemID)),
      celija(tekst(s.itemName)),
      celija(tekst(s.itemUnit)),
      celija(kol(kolicina(s)), 'right'),
      celija(' ')
    ]))
  },
  layout: okvir
})

// Rad se na papiru pise bez satnice i iznosa - samo tko, kada i koliko sati,
// pa ima mjesto da majstor upise stvarno odradeno.
const tablicaRad = (stavke) => ({
  table: {
    headerRows: 1,
    widths: ['*', 70, 56, 86],
    body: [[
      zaglavljeCelija('Radnik / opis'), zaglavljeCelija('Datum'),
      zaglavljeCelija('Sati', 'right'), zaglavljeCelija('Stvarno', 'center')
    ]].concat(stavke.map(s => [
      celija(tekst(s.itemName) || tekst(s.First) + ' ' + tekst(s.Last)),
      celija(datum(s.empWorkDay)),
      celija(kol(s.empWrkHrs), 'right'),
      celija(' ')
    ]))
  },
  layout: okvir
})

const nemaStavaka = (t) => ({text: t, fontSize: 9, italics: true, color: '#777', margin: [0, 2, 0, 0]})

/**
 * Napravi i otvori PDF radnog naloga za pogon.
 *
 * @param {Object} glava    zaglavlje dokumenta (documentActivHead)
 * @param {Array}  stavke   stavke dokumenta (documentActivItems)
 * @param {Object} firma    aktivna tvrtka (companyActiv)
 * @param {Object} opcije   {skladiste}
 */
export const ispisiPogon = ({glava, stavke, firma, opcije}) => {
  const g = glava || {}
  const f = firma || {}
  const o = opcije || {}
  const sve = Array.isArray(stavke) ? stavke : []

  const materijal = skupina(sve, MATERIJAL)
  const rad = skupina(sve, RAD)
  const proizvodi = skupina(sve, PROIZVEDENO)

  const sadrzaj = []

  // Zaglavlje: broj naloga je namjerno velik - na papiru u pogonu je to prvo
  // sto se trazi.
  sadrzaj.push({
    columns: [
      {
        width: '*',
        stack: [
          {text: 'RADNI NALOG', fontSize: 20, bold: true},
          {text: tekst(f.CompanyName), fontSize: 10, margin: [0, 2, 0, 0]},
          {text: o.skladiste ? 'Skladište: ' + tekst(o.skladiste) : '', fontSize: 9, color: '#555'}
        ]
      },
      {
        width: 140,
        stack: [
          {text: tekst(g.documentId), fontSize: 28, bold: true, alignment: 'right'},
          {text: datum(g.documentDate), fontSize: 10, alignment: 'right'}
        ]
      }
    ]
  })
  sadrzaj.push({
    canvas: [{type: 'line', x1: 0, y1: 2, x2: 515, y2: 2, lineWidth: 1.5, lineColor: '#000'}],
    margin: [0, 6, 0, 8]
  })

  const partner = tekst(g.partnerName)
  const podaci = []
  if (partner) { podaci.push({width: '*', text: [{text: 'Partner: ', bold: true}, partner], fontSize: 9}) }
  podaci.push({width: 'auto', text: [{text: 'Izradio: ', bold: true},
    (tekst(g.First) + ' ' + tekst(g.Last)).trim() || '—'], fontSize: 9})
  sadrzaj.push({columns: podaci, columnGap: 20, margin: [0, 0, 0, 2]})

  sadrzaj.push(naslovSkupine('▼  MATERIJAL ZA IZDATI'))
  sadrzaj.push(materijal.length ? tablicaMaterijal(materijal) : nemaStavaka('Materijal nije upisan.'))

  if (rad.length) {
    sadrzaj.push(naslovSkupine('■  RAD'))
    sadrzaj.push(tablicaRad(rad))
  }

  sadrzaj.push(naslovSkupine('▲  ZA IZRADITI'))
  sadrzaj.push(proizvodi.length ? tablicaProizvodi(proizvodi) : nemaStavaka('Nije upisano što se izrađuje.'))

  sadrzaj.push({text: 'NAPOMENA', fontSize: 9, bold: true, margin: [0, 16, 0, 4]})
  sadrzaj.push({
    table: {widths: ['*'], body: [[{text: ' ', margin: [0, 22, 0, 22]}]]},
    layout: okvir
  })

  sadrzaj.push({
    columns: [
      {width: '*', stack: [
        {canvas: [{type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 0.6, lineColor: '#555'}]},
        {text: 'Materijal izdao', fontSize: 8, color: '#555', margin: [0, 3, 0, 0]}
      ]},
      {width: 40, text: ' '},
      {width: '*', stack: [
        {canvas: [{type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 0.6, lineColor: '#555'}]},
        {text: 'Izradu preuzeo', fontSize: 8, color: '#555', margin: [0, 3, 0, 0]}
      ]}
    ],
    margin: [0, 40, 0, 0]
  })

  const dokument = {
    pageSize: 'A4',
    pageMargins: [40, 36, 40, 40],
    defaultStyle: {fontSize: 9},
    content: sadrzaj,
    footer: (strana, ukupno) => ({
      columns: [
        {text: 'Ispis: ' + dayjs().format('DD.MM.YYYY HH:mm'), fontSize: 7, color: '#777', margin: [40, 0, 0, 0]},
        {text: strana + ' / ' + ukupno, fontSize: 7, color: '#777', alignment: 'right', margin: [0, 0, 40, 0]}
      ]
    })
  }

  pdfMake.createPdf(dokument).open()
  return dokument
}

export default {ispisiPogon, MATERIJAL, RAD, OSTALO, PROIZVEDENO}
